import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { appointmentService, userService, clientService, firstContactService } from '../services/api';
import surveyService from '../services/surveyService';
import smartScheduleService from '../services/smartScheduleService';
import { useAuth } from '../contexts/AuthContext';
import { useAvailableTimeSlots } from '../hooks/useAvailableTimeSlots';
import { Calendar, Clock, MapPin, User, Phone, Save, ArrowLeft, AlertCircle, Plus, X, CheckCircle, XCircle, CalendarDays, Search } from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper para construir strings de data no fuso horário de Brasília (UTC-3)
// evitando distorções de fuso horário quando o navegador está em outro fuso.
// Formato retornado: "YYYY-MM-DD HH:MM:SS"
const toBrazilDateTimeString = (year, month, day, hour, minute, second) => {
  const yyyy = String(year).padStart(4, '0');
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  const hh = String(hour).padStart(2, '0');
  const mi = String(minute).padStart(2, '0');
  const ss = String(second).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

// Helper para gerar ISO string explicitamente no fuso de Brasília.
// Formato: "YYYY-MM-DDTHH:MM:SS-03:00"
const toBrazilISOString = (dateStr) => {
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return dateStr;
  const [, year, month, day, hour, minute, second] = match;
  const sec = second || '00';
  return `${year}-${month}-${day}T${hour}:${minute}:${sec}-03:00`;
};

// Função para fazer requisições API
const fetchAPI = async (endpoint, options = {}) => {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erro na requisição' }));
    throw new Error(error.message || 'Erro na requisição');
  }
  return response.json();
};

// Função para obter a data atual no fuso horário local
const getLocalDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

// Função para obter data e hora atual no formato datetime-local (YYYY-MM-DDTHH:MM)
const getLocalDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const parseAppointmentToLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hour, min] = match.map(Number);
    return new Date(year, month - 1, day, hour, min, 0, 0);
  }
  return new Date(dateStr);
};

const ALLOWED_RESPONSIBLE_ROLES = ['GERENCIA', 'RECEPCAO_PROJETOS', 'RECEPCAO_COMPRAS_AGENDA'];

const AppointmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  // Verificar se é agendamento de levantamento (vindo da fila de espera)
  const appointmentType = searchParams.get('type');
  const isLevantamento = appointmentType === 'levantamento';

  // Verificar se é agendamento de apresentação de 3D (vindo do levantamento)
  const surveyIdFromUrl = searchParams.get('survey_id');
  const isPresentation3D = Boolean(surveyIdFromUrl);

  // Estado do formulário
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    client_phone: '',
    client_address: '',
    document_id: '',
    responsible_user_id: '',
    appointment_date: '',
    duration_minutes: 60,
    notes: '',
    first_contact_id: '',
    is_internal: false,
    internal_type: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null);
  const lastSubmittedDataRef = useRef(null);
  const [dateInitialized, setDateInitialized] = useState(false);

  // Opções de agendamento interno
  const internalTypeOptions = [
    { value: 'REUNIAO', label: 'Reunião' },
    { value: 'SERVICO_EXTERNO', label: 'Serviço Externo' },
    { value: 'AUSENCIA_TEMPORARIA', label: 'Ausência Temporária' },
    { value: 'TREINAMENTO', label: 'Treinamento' },
    { value: 'MANUTENCAO', label: 'Manutenção' },
    { value: 'OUTRO', label: 'Outro' }
  ];
  const [newClientData, setNewClientData] = useState({
    full_name: '',
    apelido: '',
    email: '',
    phone: '',
    document_id: '',
    birth_date: '',
    address: ''
  });
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [expandedSlots, setExpandedSlots] = useState(false);
  const [presentedConfirm, setPresentedConfirm] = useState(null);

  // Estados para busca de CEP
  const [cepAddress, setCepAddress] = useState({
    cep: '',
    street: '',
    neighborhood: '',
    city: '',
    state: '',
    complement: '',
    number: ''
  });
  const [loadingCep, setLoadingCep] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [internalParticipants, setInternalParticipants] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);

  // Buscar dados do agendamento se estiver editando
  const { data: appointmentData, isLoading: isLoadingAppointment, error: appointmentError } = useQuery({
    queryKey: ['appointment', id],
    queryFn: () => {
      console.log('🔍 Buscando agendamento com ID:', id);
      return appointmentService.getAppointment(id);
    },
    enabled: isEditing && !!id,
    onSuccess: (data) => {
        console.log('📋 onSuccess: Dados do agendamento recebidos:', data);
        // O data pode vir como resposta do Axios ou como objeto direto
        const appointment = data?.data?.appointment || data?.appointment || data;
        console.log('📋 onSuccess: Appointment extraído:', appointment);
        
        if (!appointment || !appointment.id) {
          console.error('❌ Appointment não encontrado nos dados recebidos');
          return;
        }
        
        // Formatar data/hora no timezone local para o input datetime-local
        let formattedDate = '';
        if (appointment.appointment_date) {
          const date = new Date(appointment.appointment_date);
          // Ajustar para timezone local
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
        
        const formDataToSet = {
          client_id: appointment.client_id ? String(appointment.client_id) : '',
          client_name: appointment.client_name || '',
          client_phone: appointment.client_phone || '',
          client_address: appointment.client_address || '',
          responsible_user_id: appointment.responsible_user_id ? String(appointment.responsible_user_id) : '',
          appointment_date: formattedDate,
          duration_minutes: appointment.duration_minutes || 60,
          notes: appointment.appointment_type === 'PRESENTATION_3D' ? '' : (appointment.notes || ''),
          first_contact_id: appointment.first_contact_id || '',
          is_internal: !!appointment.internal_type,
          internal_type: appointment.internal_type || ''
        };
        
        console.log('📋 onSuccess: FormData que será setado:', formDataToSet);
        setFormData(formDataToSet);
      },
    onError: (error) => {
        console.error('❌ Erro ao carregar agendamento:', error);
        toast.error('Erro ao carregar agendamento');
        navigate('/agendamentos');
    }
  });

  // Carregar dados quando appointmentData mudar (alternativa ao onSuccess que pode não estar funcionando)
  useEffect(() => {
    if (isEditing && appointmentData && !isLoadingAppointment) {
      console.log('📋 useEffect: Dados do agendamento recebidos:', appointmentData);

      // O appointmentData vem como resposta do Axios, então precisa acessar .data.appointment
      const appointment = appointmentData?.data?.appointment || appointmentData?.appointment || appointmentData;
      console.log('📋 useEffect: Appointment extraído:', appointment);

      if (!appointment || !appointment.id) {
        console.error('❌ Appointment não encontrado ou inválido nos dados recebidos');
        return;
      }

      // Formatar data/hora no timezone local para o input datetime-local
      let formattedDate = '';
      if (appointment.appointment_date) {
        const date = new Date(appointment.appointment_date);
        // Ajustar para timezone local
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      const formDataToSet = {
        client_id: appointment.client_id ? String(appointment.client_id) : '',
        client_name: appointment.client_name || '',
        client_phone: appointment.client_phone || '',
        client_address: appointment.client_address || '',
        responsible_user_id: appointment.responsible_user_id ? String(appointment.responsible_user_id) : '',
        appointment_date: formattedDate,
        duration_minutes: appointment.duration_minutes || 60,
        notes: appointment.appointment_type === 'PRESENTATION_3D' ? '' : (appointment.notes || ''),
        first_contact_id: appointment.first_contact_id || '',
        is_internal: !!appointment.internal_type,
        internal_type: appointment.internal_type || ''
      };

      console.log('📋 useEffect: FormData que será setado:', formDataToSet);
      setFormData(formDataToSet);
    }
  }, [isEditing, appointmentData, isLoadingAppointment]);

  // Carregar dados do primeiro contato se for novo agendamento
  useEffect(() => {
    const firstContactId = searchParams.get('first_contact_id');
    const clientIdParam = searchParams.get('client_id');
    const clientNameParam = searchParams.get('client_name');
    const clientPhoneParam = searchParams.get('client_phone');
    const clientAddressParam = searchParams.get('client_address');

    if (!isEditing && !dateInitialized) {
      // Definir data atual local para novos agendamentos (com horário fixo 09:00)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      // Usar 09:00 como horário padrão para novos agendamentos
      const defaultDateTime = `${year}-${month}-${day}T09:00`;

      // Definir a data de hoje com hora 09:00 apenas uma vez
      setFormData(prev => ({
        ...prev,
        appointment_date: defaultDateTime
      }));
      setDateInitialized(true);

      // Se tiver client_id da URL (ex:来自 survey), usar diretamente
      if (clientIdParam) {
        console.log('📋 Client ID da URL:', clientIdParam);
        setFormData(prev => ({
          ...prev,
          client_id: clientIdParam,
          client_name: clientNameParam || prev.client_name,
          client_phone: clientPhoneParam || prev.client_phone,
          client_address: clientAddressParam || prev.client_address
        }));
      } else if (clientNameParam || clientPhoneParam) {
        // Procurar o cliente na lista de clientes pelo nome ou telefone
        const normalizedName = clientNameParam?.toLowerCase().trim();
        const normalizedPhone = clientPhoneParam?.replace(/\D/g, '');

        const foundClient = clients.find(client => {
          const clientNameMatch = client.full_name?.toLowerCase().trim() === normalizedName;
          const clientPhoneMatch = client.phone?.replace(/\D/g, '') === normalizedPhone;
          return clientNameMatch || clientPhoneMatch;
        });

        console.log('🔍 Procurando cliente:', { clientNameParam, clientPhoneParam, clients: clients.length, foundClient });

        setFormData(prev => ({
          ...prev,
          client_id: foundClient ? String(foundClient.id) : '',
          client_name: clientNameParam || prev.client_name,
          client_phone: clientPhoneParam || prev.client_phone,
          client_address: clientAddressParam || prev.client_address
        }));
      } else if (clientAddressParam) {
        setFormData(prev => ({
          ...prev,
          client_address: clientAddressParam || prev.client_address
        }));
      }

      if (firstContactId) {
        console.log('📋 Carregando dados do primeiro contato:', firstContactId);
        firstContactService.getFirstContact(firstContactId)
          .then(response => {
            const firstContact = response.data?.data || response.data;
            console.log('📋 Primeiro contato carregado:', firstContact);

            if (firstContact) {
              // Atualizar o formulário com dados do primeiro contato
              console.log('📋 Dados do primeiro contato recebidos:', firstContact);

              setFormData(prev => {
                const clientIdValue = clientIdParam || firstContact.client_id || prev.client_id;
                console.log('📋 client_id a ser definido:', clientIdValue);

                return {
                  ...prev,
                  first_contact_id: firstContactId,
                  client_id: clientIdValue ? String(clientIdValue) : '',
                  client_name: firstContact.client_name || prev.client_name,
                  client_phone: firstContact.client_phone || prev.client_phone,
                  client_address: firstContact.client_address || prev.client_address,
                  notes: `📋 AGENDAMENTO DE LEVANTAMENTO - Primeiro Contato #${firstContact.id}
═══════════════════════════════════
👤 Cliente: ${firstContact.client_name || 'Não informado'}
📱 Telefone: ${firstContact.client_phone || 'Não informado'}
📍 Endereço: ${firstContact.client_address || 'Não informado'}
${firstContact.origin_type ? `📢 Origem: ${firstContact.origin_type}` : ''}
${firstContact.potential ? `⭐ Potencial: ${firstContact.potential}` : ''}
${firstContact.service_type ? `🛠️ Tipo de Serviço: ${firstContact.service_type}` : ''}
${firstContact.estimated_budget ? `💰 Orçamento Estimado: R$ ${Number(firstContact.estimated_budget).toLocaleString('pt-BR')}` : ''}
${firstContact.desired_deadline ? `📅 Prazo Desejado: ${new Date(firstContact.desired_deadline).toLocaleDateString('pt-BR')}` : ''}
${firstContact.notes ? `📝 Observações: ${firstContact.notes}` : ''}
${firstContact.referred_by_name ? `🤝 Indicado por: ${firstContact.referred_by_name}` : ''}`
                };
              });
            }
          })
          .catch(error => {
            console.error('❌ Erro ao carregar primeiro contato:', error);
            toast.error('Erro ao carregar dados do primeiro contato');
          });
      }
    }
  }, [isEditing, searchParams, dateInitialized]);

  // Buscar usuários para seleção de responsável (deve vir antes do useEffect que usa usersData)
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getUsers({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // Buscar configuração da Agenda de Levantamento (apenas se for levantamento)
  const { data: levantamentoConfig } = useQuery({
    queryKey: ['levantamento-schedule-config'],
    queryFn: () => fetchAPI('/smart-schedule/config'),
    enabled: isLevantamento,
    staleTime: 10 * 60 * 1000,
  });

  // Normalizar lista de usuários (funcionários)
  const usersList = useMemo(() => {
    if (!usersData) return [];

    // Formato: { data: { users: [...], pagination: {...} } }
    if (Array.isArray(usersData?.data?.users)) {
      return usersData.data.users;
    }

    // Formato alternativo: { data: { data: { users: [...] } } }
    if (Array.isArray(usersData?.data?.data?.users)) {
      return usersData.data.data.users;
    }

    // Formato alternativo simples: { data: [...] }
    if (Array.isArray(usersData?.data)) {
      return usersData.data;
    }

    // Se usersData já é um array direto
    if (Array.isArray(usersData)) {
      return usersData;
    }

    return [];
  }, [usersData]);

  // Usar usersList em vez de users no select
  const users = usersList;

  // Lista de responsáveis disponíveis (GERENCIA, RECEPCAO_PROJETOS e RECEPCAO_COMPRAS_AGENDA)
  const availableResponsibles = useMemo(() => {
    const list = users.filter(u => ALLOWED_RESPONSIBLE_ROLES.includes(u.role) && u.is_active);
    if (formData.responsible_user_id) {
      const current = users.find(u => u.id === parseInt(formData.responsible_user_id));
      if (current && !list.find(u => u.id === current.id)) {
        return [current, ...list];
      }
    }
    return list;
  }, [users, formData.responsible_user_id]);

  // Pré-selecionar usuário RECEPCAO_PROJETOS em novos agendamentos
  useEffect(() => {
    if (!isEditing && user && user.role === 'RECEPCAO_PROJETOS' && user.id && !formData.responsible_user_id) {
      console.log('📋 Pré-selecionando usuário RECEPCAO_PROJETOS:', user.full_name, user.id);
      setFormData(prev => ({
        ...prev,
        responsible_user_id: String(user.id)
      }));
    }
  }, [isEditing, user, formData.responsible_user_id]);

  // Pré-selecionar gerente quando for novo agendamento a partir de first_contact
  useEffect(() => {
    const firstContactId = searchParams.get('first_contact_id');

    if (!isEditing && firstContactId && usersList.length > 0 && formData.first_contact_id) {
      const gerenciaUsers = usersList.filter(user => user.role === 'GERENCIA' && user.is_active);

      if (gerenciaUsers.length > 0 && !formData.responsible_user_id) {
        console.log('📋 Pré-selecionando gerente:', gerenciaUsers[0].full_name);
        setFormData(prev => ({
          ...prev,
          responsible_user_id: String(gerenciaUsers[0].id)
        }));
      }
    }
  }, [isEditing, searchParams, usersList, formData.first_contact_id, formData.responsible_user_id]);

  // Pré-selecionar usuário atual quando for agendamento de apresentação (survey_id na URL)
  useEffect(() => {
    const surveyId = searchParams.get('survey_id');

    if (!isEditing && surveyId && user && (user.role === 'RECEPCAO_PROJETOS' || user.role === 'GERENCIA') && !formData.responsible_user_id) {
      console.log('📋 Pré-selecionando usuário atual para apresentação:', user.full_name, user.id);
      setFormData(prev => ({
        ...prev,
        responsible_user_id: String(user.id)
      }));
    }
  }, [isEditing, searchParams, user, formData.responsible_user_id]);

  // Buscar clientes para seleção
  const { data: clientsData, refetch: refetchClients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // Normalizar lista de clientes (backend: { data: [...], pagination })
  const clients = useMemo(() => {
    if (!clientsData) return [];
    const raw = clientsData.data;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data;
    return [];
  }, [clientsData]);

  // Auto-selecionar cliente quando os clientes carregarem e houver client_name/client_phone na URL
  useEffect(() => {
    // Só executar se não estiver editando, se já tiver clientes e se ainda não tiver cliente selecionado
    if (isEditing || clients.length === 0 || formData.client_id) return;

    const clientNameParam = searchParams.get('client_name');
    const clientPhoneParam = searchParams.get('client_phone');

    if (!clientNameParam && !clientPhoneParam) return;

    // Procurar o cliente na lista de clientes pelo nome, apelido ou telefone
    const normalizedName = clientNameParam?.toLowerCase().trim();
    const normalizedPhone = clientPhoneParam?.replace(/\D/g, '');

    const foundClient = clients.find(client => {
      const clientNameMatch = client.full_name?.toLowerCase().trim() === normalizedName;
      const clientApelidoMatch = client.apelido?.toLowerCase().trim() === normalizedName;
      const clientPhoneMatch = client.phone?.replace(/\D/g, '') === normalizedPhone;
      return clientNameMatch || clientApelidoMatch || clientPhoneMatch;
    });

    if (foundClient) {
      console.log('🔍 Cliente encontrado automaticamente:', foundClient);
      setFormData(prev => ({
        ...prev,
        client_id: String(foundClient.id),
        client_name: clientNameParam || prev.client_name,
        client_phone: clientPhoneParam || prev.client_phone
      }));
    }
  }, [isEditing, clients, formData.client_id, searchParams]);

  // Mutação para criar novo cliente
  const createClientMutation = useMutation({
    mutationFn: (data) => clientService.createClient(data),
    onSuccess: (response) => {
      const newClient = response.data?.client || response.data;
      toast.success('Cliente cadastrado com sucesso!');
      setShowClientModal(false);
      setNewClientData({
        full_name: '',
        email: '',
        phone: '',
        document_id: '',
        birth_date: '',
        address: ''
      });
      // Atualizar lista de clientes
      refetchClients();
      // Selecionar o cliente recém-cadastrado
      if (newClient && newClient.id) {
        handleClientSelect(newClient.id.toString());
        // Preencher dados do cliente no formulário
        setFormData(prev => ({
          ...prev,
          client_id: newClient.id.toString(),
          client_name: newClient.full_name || newClient.name || '',
          client_phone: newClient.phone || '',
          client_address: newClient.address || ''
        }));
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao cadastrar cliente');
    },
  });

  // Calcular horários disponíveis baseado nas jornadas de trabalho
  // Usar apenas a parte de data (YYYY-MM-DD) sem converter para UTC,
  // para evitar deslocamentos de fuso horário
  const appointmentDate = formData.appointment_date
    ? formData.appointment_date.slice(0, 10)
    : null;

  // Definir duração padrão do levantamento quando o responsável for selecionado
  useEffect(() => {
    if (isLevantamento && levantamentoConfig?.data && formData.responsible_user_id && !isEditing) {
      const defaultDuration = levantamentoConfig.data.default_duration_minutes || 60;
      if (formData.duration_minutes !== defaultDuration) {
        updateField('duration_minutes', defaultDuration.toString());
      }
    }
  }, [isLevantamento, levantamentoConfig, formData.responsible_user_id]);

  // Definir primeiro dia e horário disponível quando o responsável for selecionado (apenas para levantamento)
  useEffect(() => {
    if (!isLevantamento || !levantamentoConfig?.data || !formData.responsible_user_id || isEditing) return;

    const config = levantamentoConfig.data;
    const availableDays = config.available_days || [6]; // Default: sexta
    const timeWindows = config.time_windows || [{ start: '09:00', end: '12:00' }, { start: '14:00', end: '17:00' }];
    const allowSameDay = config.allow_same_day !== false;

    // Encontrar o primeiro dia disponível a partir de hoje
    const today = new Date();
    let foundDate = null;

    for (let i = 0; i <= (config.search_ahead_days || 14); i++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() + i);
      const jsDay = checkDate.getDay(); // 0=Dom, ..., 6=Sáb
      const oneBasedDay = jsDay + 1; // 1=Dom, ..., 7=Sáb

      // Se não permite mesmo dia e é hoje, pular
      if (!allowSameDay && i === 0) continue;

      // Verificar se é dia disponível (compatível com índice 0-6 ou 1-7)
      if (availableDays.includes(jsDay) || availableDays.includes(oneBasedDay)) {
        foundDate = checkDate;
        break;
      }
    }

    if (foundDate) {
      // Pegar o primeiro horário disponível da primeira janela
      const firstWindow = timeWindows[0];
      if (firstWindow) {
        const [hours, minutes] = firstWindow.start.split(':');
        foundDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        const formattedDate = foundDate.toISOString().split('T')[0]; // YYYY-MM-DD
        const formattedTime = `${String(hours).padStart(2, '0')}:${minutes}`;

        // Atualizar data e horário
        if (formData.appointment_date !== `${formattedDate}T${formattedTime}`) {
          updateField('appointment_date', `${formattedDate}T${formattedTime}`);
        }
      }
    }
  }, [isLevantamento, levantamentoConfig, formData.responsible_user_id, isEditing]);

  const {
    availableSlots,
    isTimeSlotAvailable,
    isLoading: isLoadingSlots,
    isLoadingSchedules,
    isLoadingAppointments
  } = useAvailableTimeSlots(
    formData.responsible_user_id,
    appointmentDate,
    isEditing ? id : null
  );

  // Buscar agendamentos do dia para validação de conflitos
  const { data: dayAppointmentsData } = useQuery({
    queryKey: ['appointments-day', formData.responsible_user_id, appointmentDate, isEditing ? id : null],
    queryFn: () => {
      if (!formData.responsible_user_id || !appointmentDate) return { data: [] };

      console.log('🔍 Buscando agendamentos do dia:', { appointmentDate, responsible_user_id: formData.responsible_user_id });

      // Construir strings com fuso de Brasília explícito (UTC-3) para evitar distorções
      const dateParts = appointmentDate.split('-');
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10);
      const day = parseInt(dateParts[2], 10);

      return appointmentService.getAppointments({
        responsible_user_id: formData.responsible_user_id,
        date_from: toBrazilDateTimeString(year, month, day, 0, 0, 0),
        date_to: toBrazilDateTimeString(year, month, day, 23, 59, 59),
        limit: 100
      });
    },
    enabled: !!formData.responsible_user_id && !!appointmentDate,
    staleTime: 30000,
  });

  // Função para verificar se um slot tem conflito com agendamentos existentes
  const hasSlotConflict = (slotStart, slotEnd, durationMinutes) => {
    // Validação defensiva: garantir que data é um array
    let appointments = dayAppointmentsData?.data;

    // Normalizar formato dos dados
    if (!appointments) {
      console.log('⚠️ hasSlotConflict: appointments é undefined');
      return false;
    }
    if (appointments.data) {
      appointments = appointments.data;
    }
    if (!Array.isArray(appointments)) {
      console.log('⚠️ hasSlotConflict: appointments não é array', typeof appointments);
      return false;
    }
    if (appointments.length === 0) {
      console.log('⚠️ hasSlotConflict: Nenhum agendamento encontrado');
      return false;
    }

    console.log('🔍 hasSlotConflict verificando:', { slotStart, slotEnd, durationMinutes, appointments });

    // Filtrar agendamentos para remover o agendamento atual (se estiver editando)
    const existingAppointments = appointments.filter(apt =>
      !isEditing || apt.id !== parseInt(id)
    );

    const slotStartMinutes = parseInt(slotStart.split(':')[0]) * 60 + parseInt(slotStart.split(':')[1]);
    const slotEndMinutes = slotStartMinutes + durationMinutes;
    const bufferMinutes = 30; // 30 minutos de buffer

    for (const apt of existingAppointments) {
      if (apt.status === 'CANCELLED' || apt.status === 'REJECTED') continue;
      const aptDate = parseAppointmentToLocalDate(apt.appointment_date);
      if (!aptDate) continue;

      const aptStartMinutes = aptDate.getHours() * 60 + aptDate.getMinutes();
      const aptDuration = parseInt(apt.duration_minutes, 10) || 60;
      const aptEndMinutes = aptStartMinutes + aptDuration + bufferMinutes;

      console.log('🔍 Verificando conflito:', {
        slotStartMinutes,
        slotEndMinutes,
        aptStartMinutes,
        aptEndMinutes,
        aptDate: apt.appointment_date,
        aptDateLocal: aptDate.toString(),
        aptLocalHours: aptDate.getHours()
      });

      // Verificar sobreposição - slot começa antes do fim do apt E slot termina depois do início do apt
      const overlaps = (slotStartMinutes < aptEndMinutes && slotEndMinutes > aptStartMinutes);
      if (overlaps) {
        console.log('❌ CONFLITO ENCONTRADO!');
        return true;
      }
    }

    return false;
  };

  // Gerar slots disponíveis para levantamento baseados nas time_windows da Agenda de Levantamento
  const levantamentoSlots = useMemo(() => {
    if (!isLevantamento || !levantamentoConfig?.data) return [];

    const config = levantamentoConfig.data;
    const timeWindows = config.time_windows || [{ start: '09:00', end: '12:00' }, { start: '14:00', end: '17:00' }];
    const slotDuration = config.default_duration_minutes || 60;
    const slotStep = 30; // Gerar slots de 30 em 30 minutos

    const slots = [];
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const isToday = appointmentDate === todayStr;

    // Se não é hoje ou se allow_same_day é true
    const allowSameDay = config.allow_same_day !== false;

    if (isToday && !allowSameDay) {
      return []; // Não permite agendar para hoje
    }

    // Verificar intervalo de almoço (11:30 - 13:00)
    const lunchStart = 11 * 60 + 30; // 11:30 em minutos
    const lunchEnd = 13 * 60; // 13:00 em minutos

    for (const window of timeWindows) {
      const [startHour, startMin] = window.start.split(':').map(Number);
      const [endHour, endMin] = window.end.split(':').map(Number);

      let currentHour = startHour;
      let currentMin = startMin;

      while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
        const currentTimeInMinutes = currentHour * 60 + currentMin;

        // Se é hoje, não mostrar horários anteriores
        if (isToday) {
          const nowInMinutes = now.getHours() * 60 + now.getMinutes();
          if (currentTimeInMinutes <= nowInMinutes + 15) {
            const nextMin = currentMin + slotStep;
            currentHour += Math.floor(nextMin / 60);
            currentMin = nextMin % 60;
            continue;
          }
        }

        const slotStart = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
        let slotEndMinutesTotal = currentMin + slotDuration;
        let slotEndHour = currentHour + Math.floor(slotEndMinutesTotal / 60);
        let slotEndMin = slotEndMinutesTotal % 60;
        const slotEndTimeInMinutes = slotEndHour * 60 + slotEndMin;

        // Não ultrapassar o fim da janela
        if (slotEndHour > endHour || (slotEndHour === endHour && slotEndMin > endMin)) {
          break;
        }

        const slotEnd = `${String(slotEndHour).padStart(2, '0')}:${String(slotEndMin).padStart(2, '0')}`;

        // Verificar se o slot cai no intervalo de almoço
        const isInLunch = (currentTimeInMinutes >= lunchStart && currentTimeInMinutes < lunchEnd) ||
                         (slotEndTimeInMinutes > lunchStart && slotEndTimeInMinutes <= lunchEnd) ||
                         (currentTimeInMinutes <= lunchStart && slotEndTimeInMinutes >= lunchEnd);

        // Verificar se o slot tem conflito com agendamentos existentes
        const isOccupied = !isInLunch && hasSlotConflict(slotStart, slotEnd, slotDuration);

        // Se está no horário de almoço ou ocupado, marcar como indisponível
        const isUnavailable = isInLunch || isOccupied;

        slots.push({
          formatted: slotStart,
          start: slotStart,
          end: slotEnd,
          duration_minutes: slotDuration,
          available: !isUnavailable,
          occupied: isUnavailable,
          reason: isInLunch ? 'Almoço' : (isOccupied ? 'Ocupado' : null)
        });

        // Avançar para próximo slot (de 30 em 30 minutos)
        const nextMin = currentMin + slotStep;
        currentHour += Math.floor(nextMin / 60);
        currentMin = nextMin % 60;
      }
    }

    return slots;
  }, [isLevantamento, levantamentoConfig, appointmentDate, dayAppointmentsData]);

  // Usar slots do levantamento ou slots normais
  const displayedSlots = isLevantamento ? levantamentoSlots : availableSlots;

  // Mostrar indicador de carregamento
  const isLoadingAll = isLoadingSlots || isLoadingSchedules || isLoadingAppointments;

  // Horários disponíveis para remarcação (usar data de remarcação se estiver ativo)
  const rescheduleDateForHook = isRescheduling && rescheduleDate ? rescheduleDate : null;
  const { availableSlots: rescheduleSlots, isLoading: isLoadingRescheduleSlots } = useAvailableTimeSlots(
    formData.responsible_user_id,
    rescheduleDateForHook,
    id // Excluir o próprio agendamento
  );

  // Mutação para criar/atualizar agendamento
  const saveMutation = useMutation({
    mutationFn: (data) => {
      if (isEditing) {
        return appointmentService.updateAppointment(id, data);
      } else {
        return appointmentService.createAppointment(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      // Invalidar cache de clientes também, pois pode ter criado um cliente automaticamente
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Invalidar cache da waiting list para atualizar status
      queryClient.invalidateQueries({ queryKey: ['waiting-list'] });
      toast.success(isEditing ? 'Agendamento atualizado com sucesso!' : 'Agendamento criado com sucesso!');
      navigate('/agendamentos');
    },
    onError: (error) => {
      console.error('❌ Erro ao salvar agendamento:', error.response?.data);
      const errorData = error.response?.data;
      const errorStatus = error.response?.status;

      // Log detalhado do erro
      console.log('📝 Detalhes do erro:', JSON.stringify(errorData, null, 2));

      // Verificar se é erro de conflito (409)
      if (errorStatus === 409 && (errorData?.conflicts || errorData?.conflict)) {
        // Mostrar modal de conflito
        setConflictData(errorData.conflicts || errorData.conflictDetails?.conflictingAppointments || []);
        setPendingAppointmentData(errorData.data || lastSubmittedDataRef.current || formData);
        setShowConflictModal(true);
        return;
      }

      // Mostrar erros de validação específicos
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        const errorMessages = errorData.errors.map(e => `${e.path || e.param}: ${e.msg}`).join(', ');
        toast.error(`Validação: ${errorMessages}`);
        console.log('📝 Erros de validação:', errorData.errors);
        const validationErrors = {};
        errorData.errors.forEach(err => {
          validationErrors[err.path || err.param] = err.msg;
        });
        setErrors(validationErrors);
      } else {
        const errorMessage = errorData?.message || 'Erro ao salvar agendamento';
        toast.error(errorMessage);
      }
    },
  });

  // Mutação para atualizar status do agendamento
  const updateStatusMutation = useMutation({
    mutationFn: (status) => appointmentService.updateAppointmentStatus(id, status),
    onSuccess: (data, status) => {
      queryClient.invalidateQueries(['appointments']);
      queryClient.invalidateQueries(['appointment', id]);
      queryClient.invalidateQueries(['surveys']); // Invalidar levantamentos também
      queryClient.invalidateQueries(['clients']); // Invalidar clientes também

      // Verificar se é um agendamento de Levantamento e se o status foi alterado para COMPLETED
      const appointment = appointmentData?.data?.appointment || appointmentData?.appointment;
      if (appointment?.appointment_type === 'LEVANTAMENTO' && status === 'COMPLETED') {
        // O backend pode retornar os dados do levantamento criado
        const surveyCode = data?.data?.survey_code;
        if (surveyCode) {
          toast.success(`Levantamento criado automaticamente! Código: ${surveyCode}`);
        } else {
          toast.success('Status do agendamento atualizado com sucesso! Levantamento criado automaticamente.');
        }
      } else {
        toast.success('Status do agendamento atualizado com sucesso!');
      }
      navigate('/agendamentos');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Erro ao atualizar status do agendamento';
      toast.error(errorMessage);
    },
  });

  // Mutação para remarcar agendamento
  const rescheduleMutation = useMutation({
    mutationFn: (data) => appointmentService.reschedulePresentationAppointment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments']);
      queryClient.invalidateQueries(['appointment', id]);
      queryClient.invalidateQueries(['clients']); // Invalidar clientes também
      toast.success('Agendamento remarcado com sucesso!');
      setIsRescheduling(false);
      setRescheduleDate('');
      setRescheduleTime('');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Erro ao remarcar agendamento';
      toast.error(errorMessage);
    },
  });

  // Função para atualizar campo do formulário
  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Função para aplicar máscara de CEP
  const maskCep = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d)/, '$1-$2').replace(/(-\d{3})\d+?$/, '$1');
  };

  // Função para buscar CEP via API ViaCEP
  const fetchCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      toast.error('CEP deve conter 8 dígitos');
      return;
    }

    setLoadingCep(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/utils/cep/${cleanCep}`);
      const data = await response.json();

      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      // Preencher campos automaticamente
      setCepAddress({
        ...cepAddress,
        cep: maskCep(cep),
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        complement: data.complemento || ''
      });

      toast.success('CEP encontrado! Preencha o número se necessário.');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setLoadingCep(false);
    }
  };

  // Função para construir endereço completo
  const buildFullAddress = () => {
    const parts = [];
    if (cepAddress.street) parts.push(cepAddress.street);
    if (cepAddress.number) parts.push(`nº ${cepAddress.number}`);
    if (cepAddress.complement) parts.push(cepAddress.complement);
    if (cepAddress.neighborhood) parts.push(cepAddress.neighborhood);
    if (cepAddress.city) parts.push(cepAddress.city);
    if (cepAddress.state) parts.push(cepAddress.state);
    if (cepAddress.cep) parts.push(`CEP: ${cepAddress.cep}`);

    return parts.join(', ');
  };

  // Atualizar client_address quando CEP mudar (apenas se houver dados válidos)
  useEffect(() => {
    // Só atualiza se tiver dados significativos do CEP (não apenas CEP vazio)
    if ((cepAddress.street || cepAddress.city || cepAddress.neighborhood) && (cepAddress.street || cepAddress.city)) {
      const fullAddress = buildFullAddress();
      if (fullAddress && fullAddress.length >= 5) {
        updateField('client_address', fullAddress);
      }
    }
  }, [cepAddress]);

  // Função para selecionar cliente
  const handleClientSelect = (clientId) => {
    // Limpar campos de CEP ao trocar de cliente
    setCepAddress({
      cep: '',
      street: '',
      neighborhood: '',
      city: '',
      state: '',
      complement: '',
      number: ''
    });

    if (!clientId) {
      // Se não selecionou cliente, limpar campos relacionados
      setFormData(prev => ({
        ...prev,
        client_id: '',
        // Não limpar client_name, client_phone, client_address se já estiverem preenchidos
        // (podem ter sido preenchidos manualmente ou vindo de agendamento existente)
      }));
      return;
    }

    const selectedClient = clients.find(client => client.id === parseInt(clientId, 10));
    if (selectedClient) {
      setFormData(prev => ({
        ...prev,
        client_id: clientId,
        client_name: selectedClient.full_name,
        client_phone: selectedClient.phone || '',
        client_address: selectedClient.address || '',
        document_id: selectedClient.document_id || ''
      }));
    }
  };

  // Função para validar formulário
  const validateForm = () => {
    const newErrors = {};

    // Se for agendamento interno, validar tipo interno
    if (formData.is_internal) {
      if (!formData.internal_type) {
        newErrors.internal_type = 'Selecione o tipo de agendamento interno';
      }
      if (!formData.client_name.trim()) {
        newErrors.client_name = 'Nome/Descrição do evento é obrigatório';
      }
    } else {
      // Cliente é obrigatório (tem que selecionar um cliente)
      if (!formData.client_id) {
        newErrors.client_id = 'Selecione um cliente';
      }

      if (!formData.client_name.trim()) {
        newErrors.client_name = 'Nome do cliente é obrigatório';
      }

      // CPF/CNPJ é opcional (se fornecido, deve ter 11 ou 14 dígitos)
      const docClean = (formData.document_id || '').replace(/\D/g, '');
      if (docClean && docClean.length !== 11 && docClean.length !== 14) {
        newErrors.document_id = 'CPF ou CNPJ inválido (deve ter 11 ou 14 dígitos)';
      }

      if (!formData.client_address.trim()) {
        newErrors.client_address = 'Endereço é obrigatório';
      } else if (formData.client_address.trim().length < 5) {
        newErrors.client_address = 'Endereço deve ter pelo menos 5 caracteres';
      }
    }

    if (!formData.appointment_date) {
      newErrors.appointment_date = 'Data do agendamento é obrigatória';
    } else {
      const appointmentDate = new Date(formData.appointment_date);

      // Validar se é um dia disponível para levantamento (se for levantamento)
      if (isLevantamento && levantamentoConfig?.data) {
        const config = levantamentoConfig.data;
        const availableDays = config.available_days || [6];
        const jsDay = appointmentDate.getDay(); // 0=Dom, ..., 6=Sáb
        const oneBasedDay = jsDay + 1; // 1=Dom, ..., 7=Sáb

        if (!availableDays.includes(jsDay) && !availableDays.includes(oneBasedDay)) {
          const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
          newErrors.appointment_date = `Levantamentos só podem ser agendados em: ${availableDays.map(d => dayNames[d % 7]).join(', ')}`;
        }
      }

      // Se não tem erro de dia, validar outros erros
      if (!newErrors.appointment_date) {
        // Usar hora local para validação - só exigir data futura se for novo agendamento
        const now = new Date();
        if (!isEditing && appointmentDate <= now) {
          newErrors.appointment_date = 'Data do agendamento deve ser futura';
        }

        // Validar se o horário está disponível baseado na jornada de trabalho
        if (formData.responsible_user_id) {
          // Para levantamento, verificar se o slot selecionado está ocupado
          if (isLevantamento && displayedSlots.length > 0) {
            const selectedTime = new Date(formData.appointment_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            const selectedSlot = displayedSlots.find(s => s.formatted === selectedTime);
            if (selectedSlot?.occupied) {
              newErrors.appointment_date = 'Este horário já está ocupado por outro agendamento';
            } else if (formData.appointment_date && !isTimeSlotAvailable(formData.appointment_date, formData.duration_minutes)) {
              newErrors.appointment_date = 'Horário não disponível ou conflita com outro agendamento';
            }
          } else if (formData.appointment_date && !isTimeSlotAvailable(formData.appointment_date, formData.duration_minutes)) {
            newErrors.appointment_date = 'Horário não disponível ou conflita com outro agendamento';
          }
        }
      }
    }

    if (!formData.responsible_user_id) {
      newErrors.responsible_user_id = 'Responsável é obrigatório';
    }

    if (formData.duration_minutes < 15 || formData.duration_minutes > 480) {
      newErrors.duration_minutes = 'Duração deve ser entre 15 e 480 minutos';
    }

    console.log('🔍 validateForm newErrors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Função para submeter formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Não enviar client_id se for null ou vazio (o backend não aceita null)
      const dataToSend = {
        ...formData,
        client_id: formData.client_id && !formData.is_internal ? parseInt(formData.client_id) : undefined,
        responsible_user_id: formData.responsible_user_id ? parseInt(formData.responsible_user_id) : undefined,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : undefined,
        first_contact_id: formData.first_contact_id && !formData.is_internal ? parseInt(formData.first_contact_id) : undefined,
        internal_type: formData.is_internal ? formData.internal_type : undefined,
        internal_participants: formData.is_internal && internalParticipants?.length > 0 ? internalParticipants : undefined,
      };

      // Remover campos undefined
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === undefined) {
          delete dataToSend[key];
        }
      });

      lastSubmittedDataRef.current = dataToSend;
      console.log('📤 Enviando dados para API:', dataToSend);

      // Se for agendamento de levantamento, usar smartScheduleService para respeitar a configuração
      if (isLevantamento) {
        // Validação: Levantamento requer first_contact_id
        if (!dataToSend.first_contact_id) {
          toast.error('Levantamento requer um Primeiro Contato selecionado');
          setIsSubmitting(false);
          return;
        }

        // Extrair data e horário do campo appointment_date (formato YYYY-MM-DDTHH:MM)
        const [datePart, timePart] = formData.appointment_date.split('T');

        await smartScheduleService.scheduleLevantamento({
          first_contact_id: dataToSend.first_contact_id,
          slot_date: datePart, // YYYY-MM-DD
          slot_time: timePart || '09:00', // HH:MM ou padrão 09:00
          duration_minutes: dataToSend.duration_minutes,
          create_survey: true,
          notes: dataToSend.notes,
          responsible_user_id: dataToSend.responsible_user_id
        });
        // Sucesso - redirecionar
        queryClient.invalidateQueries({ queryKey: ['appointments'] });
        queryClient.invalidateQueries({ queryKey: ['waiting-list'] });
        toast.success('Agendamento de levantamento criado com sucesso!');
        navigate('/agendamentos');
      } else if (isPresentation3D) {
        // Se for apresentação de 3D, usar createPresentationAppointment
        await surveyService.createPresentationAppointment(surveyIdFromUrl, {
          responsible_user_id: dataToSend.responsible_user_id,
          appointment_date: formData.appointment_date,
          duration_minutes: dataToSend.duration_minutes,
          notes: dataToSend.notes
        });
        queryClient.invalidateQueries({ queryKey: ['appointments'] });
        queryClient.invalidateQueries({ queryKey: ['surveys'] });
        toast.success('Apresentação agendada com sucesso!');
        navigate('/levantamentos');
      } else {
        // Agendamento normal
        await saveMutation.mutateAsync({
          ...dataToSend,
          ...(dataToSend.appointment_type && { appointment_type: dataToSend.appointment_type })
        });
      }
    } catch (error) {
      // Tratamento de erro para smartSchedule
      if (isLevantamento) {
        console.error('❌ Erro ao agendar levantamento:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao agendar levantamento';
        toast.error(errorMessage);
        return;
      }
      // Para agendamento normal, o onError do mutations trata
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para forçar criação do agendamento mesmo com conflito
  const handleForceCreate = async () => {
    const dataToSubmit = pendingAppointmentData || lastSubmittedDataRef.current || formData;
    if (!dataToSubmit) return;

    setIsSubmitting(true);
    setShowConflictModal(false);

    try {
      const dataWithForce = {
        ...dataToSubmit,
        force_create: true
      };
      await saveMutation.mutateAsync(dataWithForce);
    } catch (error) {
      console.error('Erro ao forçar criação de agendamento:', error);
    } finally {
      setIsSubmitting(false);
      setShowConflictModal(false);
      setConflictData(null);
      setPendingAppointmentData(null);
    }
  };

  if (isEditing && isLoadingAppointment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-sm text-gray-600">Carregando agendamento...</p>
        </div>
      </div>
    );
  }

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const selectedDateObj = formData.appointment_date ? new Date(formData.appointment_date) : null;
  const selectedYear = selectedDateObj ? selectedDateObj.getFullYear() : new Date().getFullYear();
  const selectedMonth = selectedDateObj ? selectedDateObj.getMonth() : new Date().getMonth();
  const selectedDay = selectedDateObj ? selectedDateObj.getDate() : null;

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const handleDateSelect = (day) => {
    if (!formData.responsible_user_id) {
      toast.error('Selecione um responsável primeiro');
      return;
    }
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const currentTime = formData.appointment_date?.split('T')[1] || '09:00';
    updateField('appointment_date', `${dateStr}T${currentTime}`);
  };

  const handleTimeSelect = (time) => {
    if (!formData.appointment_date) {
      toast.error('Selecione uma data primeiro');
      return;
    }
    const datePart = formData.appointment_date.split('T')[0];
    updateField('appointment_date', `${datePart}T${time}`);
  };

  const handleMonthChange = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    if (newMonth < 0) { newMonth = 11; newYear--; }
    if (newMonth > 11) { newMonth = 0; newYear++; }
    const today = new Date();
    if (newYear < today.getFullYear() || (newYear === today.getFullYear() && newMonth < today.getMonth())) return;
    const day = 1;
    const dateStr = `${newYear}-${String(newMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const currentTime = formData.appointment_date?.split('T')[1] || '09:00';
    updateField('appointment_date', `${dateStr}T${currentTime}`);
  };

  const selectedClient = clients.find(c => c.id === parseInt(formData.client_id));
  const selectedResponsible = users.find(u => u.id === parseInt(formData.responsible_user_id));

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  };

  const durationPresets = [15, 30, 45, 60, 90, 120];
  const isCustomDuration = !durationPresets.includes(formData.duration_minutes);

  const formatSummaryDate = () => {
    if (!selectedDateObj) return '—';
    const dayName = weekDays[selectedDateObj.getDay()];
    const day = selectedDateObj.getDate();
    const monthName = monthNames[selectedDateObj.getMonth()];
    return `${dayName}, ${day} de ${monthName}`;
  };

  const formatSummaryTime = () => {
    if (!formData.appointment_date) return '—';
    const time = formData.appointment_date.split('T')[1];
    if (!time) return '—';
    const [h, m] = time.split(':');
    const durationMin = parseInt(formData.duration_minutes, 10) || 60;
    const totalMinutes = (parseInt(m, 10) || 0) + durationMin;
    const endH = (parseInt(h, 10) || 0) + Math.floor(totalMinutes / 60);
    const endM = totalMinutes % 60;
    return `${h}:${m} – ${String(endH % 24).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/agendamentos')}
                className="p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-base sm:text-lg font-semibold text-gray-900">
                  {isEditing ? 'Editar Agendamento' : 'Novo agendamento'}
                </h1>
                <p className="text-xs text-gray-500 hidden sm:block">
                  {isEditing ? 'Atualize as informações' : 'Crie um compromisso para sua agenda'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/agendamentos')}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm"
              >
                <CheckCircle className="w-4 h-4" />
                {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Appointment Type */}
          <section>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Tipo de agendamento</h2>
            <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
              <button
                type="button"
                onClick={() => {
                  if (formData.is_internal) {
                    updateField('is_internal', false);
                    setInternalParticipants([]);
                  }
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${
                  !formData.is_internal
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <User className="w-4 h-4" />
                Atendimento
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!formData.is_internal) {
                    updateField('is_internal', true);
                  }
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${
                  formData.is_internal
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Calendar className="w-4 h-4" />
                Agendamento interno
              </button>
            </div>
          </section>

          {/* Internal Type Selection */}
          {formData.is_internal && (
            <section className="bg-purple-50 rounded-xl p-4 border border-purple-100">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {internalTypeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateField('internal_type', opt.value)}
                    className={`py-2 px-3 text-sm rounded-lg border transition-all ${
                      formData.internal_type === opt.value
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {errors.internal_type && (
                <p className="text-xs text-red-600 mb-2">{errors.internal_type}</p>
              )}
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => updateField('client_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                  errors.client_name ? 'border-red-300' : 'border-gray-200'
                }`}
                placeholder="Nome ou descrição do evento *"
              />
              {errors.client_name && <p className="text-xs text-red-600 mt-1">{errors.client_name}</p>}

              {/* Participants */}
              <div className="mt-4 pt-4 border-t border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-gray-700">
                    Participantes
                  </label>
                  <span className="text-xs text-gray-500">{internalParticipants.length} selecionado(s)</span>
                </div>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={participantSearch}
                    onChange={(e) => {
                      setParticipantSearch(e.target.value);
                      setShowParticipantDropdown(true);
                    }}
                    onFocus={() => setShowParticipantDropdown(true)}
                    onBlur={() => setTimeout(() => setShowParticipantDropdown(false), 150)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                    placeholder="Buscar participantes..."
                  />
                  {showParticipantDropdown && (participantSearch.length >= 2 || participantSearch.length === 0) && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-100 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {users
                        .filter(u => {
                          if (!u.is_active || internalParticipants.includes(u.id)) return false;
                          if (participantSearch.length === 0) return true;
                          const search = participantSearch.toLowerCase();
                          return u.full_name?.toLowerCase().includes(search);
                        })
                        .slice(0, 8)
                        .map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => {
                              setInternalParticipants(prev => [...prev, user.id]);
                              setParticipantSearch('');
                              setShowParticipantDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0 flex items-center gap-2"
                          >
                            <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-semibold text-purple-600">{getInitials(user.full_name)}</span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-xs">{user.full_name}</p>
                              <p className="text-[10px] text-gray-500">{user.role === 'GERENCIA' ? 'Gerência' : user.role === 'RECEPCAO_PROJETOS' ? 'Recepção Projetos' : user.role === 'RECEPCAO_COMPRAS_AGENDA' ? 'Recepção' : user.role}</p>
                            </div>
                          </button>
                        ))}
                      {users.filter(u => {
                        if (!u.is_active || internalParticipants.includes(u.id)) return false;
                        if (participantSearch.length === 0) return true;
                        const search = participantSearch.toLowerCase();
                        return u.full_name?.toLowerCase().includes(search);
                      }).length === 0 && (
                        <p className="px-3 py-2 text-xs text-gray-500 text-center">Nenhum participante encontrado</p>
                      )}
                    </div>
                  )}
                </div>
                {internalParticipants.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {internalParticipants.map((userId) => {
                      const user = users.find(u => u.id === userId);
                      if (!user) return null;
                      return (
                        <span
                          key={userId}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs"
                        >
                          <span className="w-4 h-4 rounded-full bg-purple-200 flex items-center justify-center">
                            <span className="text-[8px] font-semibold">{getInitials(user.full_name)}</span>
                          </span>
                          {user.full_name.split(' ')[0]}
                          <button
                            type="button"
                            onClick={() => setInternalParticipants(prev => prev.filter(id => id !== userId))}
                            className="ml-0.5 text-purple-500 hover:text-purple-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Responsible */}
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Responsável</h2>
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <select
                      value={formData.responsible_user_id}
                      onChange={(e) => updateField('responsible_user_id', e.target.value)}
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm ${
                        errors.responsible_user_id ? 'border-red-300' : 'border-gray-200'
                      }`}
                    >
                      <option value="">Selecionar responsável</option>
                      {availableResponsibles.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} - ({user.role === 'RECEPCAO_PROJETOS' ? 'Recepção Projetos' : user.role === 'RECEPCAO_COMPRAS_AGENDA' ? 'Recepção' : 'Gerência'})
                        </option>
                      ))}
                    </select>
                    {errors.responsible_user_id && <p className="text-xs text-red-600 mt-1">{errors.responsible_user_id}</p>}
                    {selectedResponsible && (
                      <div className="mt-3 flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-xs font-semibold text-purple-600">{getInitials(selectedResponsible.full_name)}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{selectedResponsible.full_name}</p>
                          <p className="text-xs text-gray-500">
                            {selectedResponsible.role === 'RECEPCAO_PROJETOS' ? 'Recepção Projetos' : selectedResponsible.role === 'RECEPCAO_COMPRAS_AGENDA' ? 'Recepção' : 'Gerência'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Calendar & Time */}
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Data e horário</h2>
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <button type="button" onClick={() => handleMonthChange(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                        <ArrowLeft className="w-4 h-4 text-gray-600" />
                      </button>
                      <p className="text-sm font-semibold text-gray-900">{monthNames[selectedMonth]} {selectedYear}</p>
                      <button type="button" onClick={() => handleMonthChange(1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                        <ArrowLeft className="w-4 h-4 text-gray-600 rotate-180" />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {weekDays.map((day) => (
                        <div key={day} className="text-center text-[10px] font-medium text-gray-400 py-1">{day}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: getFirstDayOfMonth(selectedYear, selectedMonth) }, (_, i) => (
                        <div key={`empty-${i}`} className="aspect-square" />
                      ))}
                      {Array.from({ length: getDaysInMonth(selectedYear, selectedMonth) }, (_, i) => {
                        const day = i + 1;
                        const isSelected = selectedDay === day;
                        const today = new Date();
                        const isPast = new Date(selectedYear, selectedMonth, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => !isPast && handleDateSelect(day)}
                            disabled={isPast}
                            className={`aspect-square flex items-center justify-center text-xs rounded-lg transition-all ${
                              isSelected ? 'bg-purple-600 text-white font-semibold' : isPast ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    {selectedDateObj && (
                      <p className="mt-3 text-center text-sm font-medium text-gray-900 capitalize">
                        {weekDays[selectedDateObj.getDay()]}, {selectedDateObj.getDate()} de {monthNames[selectedDateObj.getMonth()]}
                      </p>
                    )}
                    {errors.appointment_date && <p className="text-xs text-red-600 mt-1 text-center">{errors.appointment_date}</p>}
                  </div>

                  {/* Time Slots */}
                  <div className="mt-3 bg-white rounded-xl border border-gray-200 p-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">Horário</p>
                    {formData.responsible_user_id && appointmentDate && displayedSlots.length > 0 && (
                      <>
                        <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto">
                          {(expandedSlots ? displayedSlots : displayedSlots.slice(0, 16)).map((slot, index) => {
                            const timeStr = `${appointmentDate}T${slot.formatted}`;
                            const selectedTimeStr = formData.appointment_date?.split('T')[1];
                            const isSelected = selectedTimeStr === slot.formatted;
                            return (
                              <button
                                key={index}
                                type="button"
                                disabled={slot.occupied}
                                onClick={() => handleTimeSelect(slot.formatted)}
                                className={`py-1.5 px-2 text-xs rounded-lg transition-all ${
                                  slot.occupied
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed line-through'
                                    : isSelected
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                              >
                                {slot.formatted}
                              </button>
                            );
                          })}
                        </div>
                        {displayedSlots.length > 16 && (
                          <button type="button" onClick={() => setExpandedSlots(!expandedSlots)} className="w-full mt-2 text-xs text-purple-600 hover:underline">
                            {expandedSlots ? 'Mostrar menos' : `+ ${displayedSlots.length - 16} mais`}
                          </button>
                        )}
                      </>
                    )}
                    {formData.responsible_user_id && appointmentDate && isLoadingAll && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                        Carregando horários...
                      </div>
                    )}
                    {!formData.responsible_user_id && (
                      <p className="text-xs text-gray-400">Selecione um responsável para ver os horários</p>
                    )}
                    {formData.responsible_user_id && appointmentDate && !isLoadingAll && displayedSlots.length === 0 && (
                      <p className="text-xs text-amber-600">Nenhum horário disponível</p>
                    )}
                  </div>
                </div>

                {/* Duration */}
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Duração</h2>
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {[15, 30, 45, 60, 90, 120].map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => updateField('duration_minutes', d.toString())}
                          className={`py-1.5 px-3 text-xs rounded-lg border transition-all ${
                            formData.duration_minutes === d
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          {d}min
                        </button>
                      ))}
                    </div>
                    {errors.duration_minutes && <p className="text-xs text-red-600 mt-1">{errors.duration_minutes}</p>}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Two Column Layout */}
          {!formData.is_internal && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Client */}
              <div className="space-y-5">
                {/* Client Selection */}
                <section className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</h2>
                    <button
                      type="button"
                      onClick={() => setShowClientModal(true)}
                      className="flex items-center gap-1 text-xs text-[#49943d] hover:text-[#3a7630] font-medium"
                    >
                      <Plus className="w-3 h-3" />
                      Novo Cliente
                    </button>
                  </div>
                  {selectedClient ? (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-600">{getInitials(selectedClient.full_name)}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{selectedClient.apelido || selectedClient.full_name}</p>
                          <p className="text-xs text-gray-500 truncate">{selectedClient.full_name}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          handleClientSelect('');
                          setClientSearch('');
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        className={`w-full pl-10 pr-3 py-2.5 border rounded-lg text-sm ${
                          errors.client_id ? 'border-red-300' : 'border-gray-200'
                        }`}
                        placeholder="Buscar por nome ou apelido..."
                      />
                      {errors.client_id && <p className="text-xs text-red-600 mt-1">{errors.client_id}</p>}
                      {clientSearch.length >= 2 && clients.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-100 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {clients
                            .filter(c => {
                              const search = clientSearch.toLowerCase();
                              return c.full_name?.toLowerCase().includes(search) || c.apelido?.toLowerCase().includes(search);
                            })
                            .slice(0, 10)
                            .map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  handleClientSelect(client.id.toString());
                                  setClientSearch('');
                                }}
                                className="w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0"
                              >
                                <p className="font-medium text-gray-900">{client.apelido || client.full_name}</p>
                                <p className="text-xs text-gray-500">{client.full_name}</p>
                              </button>
                            ))}
                          {clients.filter(c => {
                            const search = clientSearch.toLowerCase();
                            return c.full_name?.toLowerCase().includes(search) || c.apelido?.toLowerCase().includes(search);
                          }).length === 0 && (
                            <p className="px-3 py-2 text-sm text-gray-500 text-center">Nenhum cliente encontrado</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </section>

                {/* Client Info Card */}
                {selectedClient && (
                  <section className="bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-blue-600">{getInitials(selectedClient.full_name)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{selectedClient.full_name}</p>
                        {selectedClient.apelido && selectedClient.apelido !== selectedClient.full_name && (
                          <p className="text-xs text-gray-500">{selectedClient.apelido}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-blue-100 space-y-1.5">
                      {selectedClient.phone && (
                        <p className="text-xs text-gray-600 flex items-center gap-2">
                          <Phone className="w-3 h-3 text-gray-400" />
                          {selectedClient.phone}
                        </p>
                      )}
                      {selectedClient.address && (
                        <p className="text-xs text-gray-600 flex items-center gap-2">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          {selectedClient.address}
                        </p>
                      )}
                    </div>
                  </section>
                )}

                {/* Manual Client Fields */}
                <section className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Nome do Cliente *</label>
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => updateField('client_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm ${
                        errors.client_name ? 'border-red-300' : 'border-gray-200'
                      }`}
                      placeholder="Nome completo"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Telefone</label>
                    <input
                      type="tel"
                      value={formData.client_phone}
                      onChange={(e) => updateField('client_phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </section>

                {/* Address */}
                <section>
                  <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Local</h2>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                    <div className="flex gap-2">
                      <div className="w-28 flex-shrink-0">
                        <input
                          type="text"
                          value={cepAddress.cep}
                          onChange={(e) => setCepAddress({ ...cepAddress, cep: maskCep(e.target.value) })}
                          onBlur={(e) => {
                            if (e.target.value.replace(/\D/g, '').length === 8) fetchCep(e.target.value);
                          }}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="CEP"
                          maxLength={9}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => fetchCep(cepAddress.cep)}
                        disabled={loadingCep || cepAddress.cep.replace(/\D/g, '').length !== 8}
                        className="px-4 py-2 bg-[#49943d] text-white text-sm rounded-lg hover:bg-[#3d7a32] disabled:bg-gray-200 disabled:text-gray-400 flex items-center gap-1.5"
                      >
                        {loadingCep ? (
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                        ) : (
                          <MapPin className="w-3.5 h-3.5" />
                        )}
                        Buscar
                      </button>
                    </div>
                    {(cepAddress.street || cepAddress.city) && (
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={cepAddress.street}
                          onChange={(e) => setCepAddress({ ...cepAddress, street: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="Rua"
                        />
                        <input
                          type="text"
                          value={cepAddress.number}
                          onChange={(e) => setCepAddress({ ...cepAddress, number: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="Nº"
                        />
                        <input
                          type="text"
                          value={cepAddress.complement}
                          onChange={(e) => setCepAddress({ ...cepAddress, complement: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="Complemento"
                        />
                        <input
                          type="text"
                          value={cepAddress.neighborhood}
                          onChange={(e) => setCepAddress({ ...cepAddress, neighborhood: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="Bairro"
                        />
                        <input
                          type="text"
                          value={cepAddress.city}
                          onChange={(e) => setCepAddress({ ...cepAddress, city: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="Cidade"
                        />
                        <input
                          type="text"
                          value={cepAddress.state}
                          onChange={(e) => setCepAddress({ ...cepAddress, state: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm uppercase"
                          placeholder="UF"
                          maxLength={2}
                        />
                      </div>
                    )}
                    <textarea
                      value={formData.client_address}
                      onChange={(e) => updateField('client_address', e.target.value)}
                      rows={2}
                      className={`w-full px-3 py-2 border rounded-lg text-sm resize-none ${
                        errors.client_address ? 'border-red-300' : 'border-gray-200'
                      }`}
                      placeholder="Endereço completo"
                    />
                    {errors.client_address && <p className="text-xs text-red-600">{errors.client_address}</p>}
                  </div>
                </section>
              </div>

              {/* Right Column - Date/Time/Responsible */}
              <div className="space-y-5">
                {/* Responsible */}
                <section>
                  <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Responsável</h2>
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <select
                      value={formData.responsible_user_id}
                      onChange={(e) => updateField('responsible_user_id', e.target.value)}
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm ${
                        errors.responsible_user_id ? 'border-red-300' : 'border-gray-200'
                      }`}
                    >
                      <option value="">Selecionar responsável</option>
                      {availableResponsibles.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} - ({user.role === 'RECEPCAO_PROJETOS' ? 'Recepção Projetos' : user.role === 'RECEPCAO_COMPRAS_AGENDA' ? 'Recepção' : 'Gerência'})
                        </option>
                      ))}
                    </select>
                    {errors.responsible_user_id && <p className="text-xs text-red-600 mt-1">{errors.responsible_user_id}</p>}
                    {selectedResponsible && (
                      <div className="mt-3 flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-[#49943d] flex items-center justify-center">
                          <span className="text-xs font-semibold text-white">{getInitials(selectedResponsible.full_name)}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{selectedResponsible.full_name}</p>
                          <p className="text-xs text-gray-500">
                            {selectedResponsible.role === 'RECEPCAO_PROJETOS' ? 'Recepção Projetos' : selectedResponsible.role === 'RECEPCAO_COMPRAS_AGENDA' ? 'Recepção' : 'Gerência'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* Calendar & Time */}
                <section>
                  <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Data e horário</h2>
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    {isLevantamento && levantamentoConfig?.data && (() => {
                      const config = levantamentoConfig.data;
                      const availableDays = config.available_days || [6];
                      const dayNames = ['', 'Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
                      return (
                        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                          <strong>Levantamento:</strong> {availableDays.map(d => dayNames[d]).join(', ')}
                          {!config.allow_same_day && ' • Não permite mesmo dia'}
                        </div>
                      );
                    })()}
                    {/* Calendar Header */}
                    <div className="flex items-center justify-between mb-3">
                      <button
                        type="button"
                        onClick={() => handleMonthChange(-1)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg"
                      >
                        <ArrowLeft className="w-4 h-4 text-gray-600" />
                      </button>
                      <p className="text-sm font-semibold text-gray-900">
                        {monthNames[selectedMonth]} {selectedYear}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleMonthChange(1)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg"
                      >
                        <ArrowLeft className="w-4 h-4 text-gray-600 rotate-180" />
                      </button>
                    </div>
                    {/* Week Days */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {weekDays.map((day) => (
                        <div key={day} className="text-center text-[10px] font-medium text-gray-400 py-1">
                          {day}
                        </div>
                      ))}
                    </div>
                    {/* Days */}
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: getFirstDayOfMonth(selectedYear, selectedMonth) }, (_, i) => (
                        <div key={`empty-${i}`} className="aspect-square" />
                      ))}
                      {Array.from({ length: getDaysInMonth(selectedYear, selectedMonth) }, (_, i) => {
                        const day = i + 1;
                        const isSelected = selectedDay === day;
                        const today = new Date();
                        const isPast = new Date(selectedYear, selectedMonth, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => !isPast && handleDateSelect(day)}
                            disabled={isPast}
                            className={`aspect-square flex items-center justify-center text-xs rounded-lg transition-all ${
                              isSelected
                                ? 'bg-blue-600 text-white font-semibold'
                                : isPast
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'hover:bg-gray-100 text-gray-700'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    {/* Selected Date Display */}
                    {selectedDateObj && (
                      <p className="mt-3 text-center text-sm font-medium text-gray-900 capitalize">
                        {weekDays[selectedDateObj.getDay()]}, {selectedDateObj.getDate()} de {monthNames[selectedDateObj.getMonth()]}
                      </p>
                    )}
                    {errors.appointment_date && <p className="text-xs text-red-600 mt-1 text-center">{errors.appointment_date}</p>}
                  </div>

                  {/* Time Slots */}
                  <div className="mt-3 bg-white rounded-xl border border-gray-200 p-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">Horário</p>
                    {formData.responsible_user_id && appointmentDate && displayedSlots.length > 0 && (
                      <>
                        <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto">
                          {(expandedSlots ? displayedSlots : displayedSlots.slice(0, 16)).map((slot, index) => {
                            const timeStr = `${appointmentDate}T${slot.formatted}`;
                            const selectedTimeStr = formData.appointment_date?.split('T')[1];
                            const isSelected = selectedTimeStr === slot.formatted;
                            return (
                              <button
                                key={index}
                                type="button"
                                disabled={slot.occupied}
                                onClick={() => handleTimeSelect(slot.formatted)}
                                className={`py-1.5 px-2 text-xs rounded-lg transition-all ${
                                  slot.occupied
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed line-through'
                                    : isSelected
                                      ? 'bg-[#49943d] text-white'
                                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                              >
                                {slot.formatted}
                              </button>
                            );
                          })}
                        </div>
                        {displayedSlots.length > 16 && (
                          <button
                            type="button"
                            onClick={() => setExpandedSlots(!expandedSlots)}
                            className="w-full mt-2 text-xs text-[#49943d] hover:underline"
                          >
                            {expandedSlots ? 'Mostrar menos' : `+ ${displayedSlots.length - 16} mais`}
                          </button>
                        )}
                      </>
                    )}
                    {formData.responsible_user_id && appointmentDate && isLoadingAll && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                        Carregando horários...
                      </div>
                    )}
                    {!formData.responsible_user_id && (
                      <p className="text-xs text-gray-400">Selecione um responsável para ver os horários</p>
                    )}
                    {formData.responsible_user_id && appointmentDate && !isLoadingAll && displayedSlots.length === 0 && (
                      <p className="text-xs text-amber-600">Nenhum horário disponível</p>
                    )}
                  </div>
                </section>

                {/* Duration */}
                <section>
                  <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Duração</h2>
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    {isLevantamento ? (
                      <p className="text-sm text-gray-700">{formData.duration_minutes} minutos (definido pela agenda)</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {durationPresets.map((d) => (
                            <button
                              key={d}
                              type="button"
                              onClick={() => updateField('duration_minutes', d.toString())}
                              className={`py-1.5 px-3 text-xs rounded-lg border transition-all ${
                                formData.duration_minutes === d
                                  ? 'bg-[#49943d] text-white border-[#49943d]'
                                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-[#49943d]'
                              }`}
                            >
                              {d}min
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const val = prompt('Duração em minutos (15-480):', formData.duration_minutes);
                              if (val && parseInt(val) >= 15 && parseInt(val) <= 480) {
                                updateField('duration_minutes', val);
                              }
                            }}
                            className={`py-1.5 px-3 text-xs rounded-lg border transition-all ${
                              isCustomDuration
                                ? 'bg-[#49943d] text-white border-[#49943d]'
                                : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-[#49943d]'
                            }`}
                          >
                            Outro
                          </button>
                        </div>
                        {isCustomDuration && (
                          <input
                            type="number"
                            min="15"
                            max="480"
                            value={formData.duration_minutes}
                            onChange={(e) => updateField('duration_minutes', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mt-1"
                          />
                        )}
                        {errors.duration_minutes && <p className="text-xs text-red-600 mt-1">{errors.duration_minutes}</p>}
                      </>
                    )}
                  </div>
                </section>
              </div>
            </div>
          )}

          {/* Notes */}
          <section>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Detalhes</h2>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <textarea
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                placeholder="Adicione observações sobre este agendamento..."
                disabled={appointmentData?.appointment?.appointment_type === 'PRESENTATION_3D' && appointmentData?.appointment?.status !== 'COMPLETED'}
              />
              {appointmentData?.appointment?.appointment_type === 'PRESENTATION_3D' && appointmentData?.appointment?.status !== 'COMPLETED' && (
                <p className="text-xs text-gray-500 mt-1">Observações disponíveis após encerrar o atendimento</p>
              )}
            </div>
          </section>

          {/* Summary */}
          {(formData.appointment_date || formData.client_name || formData.responsible_user_id) && (
            <section className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Resumo</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Data</p>
                    <p className="text-sm font-medium text-gray-900">{formatSummaryDate()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Horário</p>
                    <p className="text-sm font-medium text-gray-900">{formatSummaryTime()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Responsável</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{selectedResponsible?.full_name?.split(' ')[0] || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Duração</p>
                    <p className="text-sm font-medium text-gray-900">{formData.duration_minutes}min</p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* 3D Presentation Actions */}
          {isEditing && appointmentData?.data?.appointment?.appointment_type === 'PRESENTATION_3D' && (() => {
            const appointment = appointmentData?.data?.appointment || appointmentData?.appointment;
            const isResponsible = user?.id === appointment?.responsible_user_id;
            const isManager = user?.role === 'GERENCIA';
            const canManage = (isResponsible && user?.role === 'RECEPCAO_PROJETOS') || isManager;
            const status = appointment?.status;
            const canMarkCompleted = canManage && (status === 'SCHEDULED' || status === 'IN_PROGRESS');
            const canCancel = canManage && (status === 'SCHEDULED' || status === 'IN_PROGRESS');
            const canReschedule = canManage && status === 'SCHEDULED';
            if (!canMarkCompleted && !canCancel && !canReschedule) return null;
            return (
              <section className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                <h2 className="text-sm font-semibold text-blue-900 mb-3">Ações do Agendamento</h2>
                <div className="flex flex-wrap gap-2">
                  {canMarkCompleted && (
                    <button
                      type="button"
                      onClick={() => setPresentedConfirm({ action: 'COMPLETED', title: 'Marcar como Apresentado', message: 'Tem certeza?', type: 'warning' })}
                      disabled={updateStatusMutation.isLoading}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {updateStatusMutation.isLoading ? 'Processando...' : 'Marcar como Apresentado'}
                    </button>
                  )}
                  {canCancel && (
                    <button
                      type="button"
                      onClick={() => setPresentedConfirm({ action: 'CANCELLED', title: 'Cancelar', message: 'Tem certeza?', type: 'danger' })}
                      disabled={updateStatusMutation.isLoading}
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Cancelar
                    </button>
                  )}
                  {canReschedule && (
                    <button
                      type="button"
                      onClick={() => setIsRescheduling(!isRescheduling)}
                      className="px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 flex items-center gap-2"
                    >
                      <CalendarDays className="w-4 h-4" />
                      {isRescheduling ? 'Cancelar' : 'Remarcar'}
                    </button>
                  )}
                </div>
                {isRescheduling && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-orange-200 space-y-3">
                    <input
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => { setRescheduleDate(e.target.value); setRescheduleTime(''); }}
                      min={getLocalDate()}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    {rescheduleDate && formData.responsible_user_id && (
                      <div className="flex flex-wrap gap-1.5">
                        {rescheduleSlots.map((slot, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setRescheduleTime(slot.formatted)}
                            className={`py-1 px-2 text-xs rounded-lg ${rescheduleTime === slot.formatted ? 'bg-orange-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                          >
                            {slot.formatted}
                          </button>
                        ))}
                      </div>
                    )}
                    {rescheduleDate && rescheduleTime && (
                      <button
                        type="button"
                        onClick={() => {
                          const [h, m] = rescheduleTime.split(':');
                          const newDateTime = new Date(`${rescheduleDate}T${h}:${m}`);
                          rescheduleMutation.mutate({ appointment_date: newDateTime.toISOString(), duration_minutes: formData.duration_minutes });
                        }}
                        disabled={rescheduleMutation.isLoading}
                        className="w-full px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:opacity-50"
                      >
                        {rescheduleMutation.isLoading ? 'Remarcando...' : 'Confirmar Remarcação'}
                      </button>
                    )}
                  </div>
                )}
              </section>
            );
          })()}
        </form>
      </main>

      {/* Modal de Cadastro Rápido de Cliente */}
      {showClientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Cadastrar Novo Cliente</h2>
              <button
                onClick={() => {
                  setShowClientModal(false);
                  setNewClientData({
                    full_name: '',
                    email: '',
                    phone: '',
                    document_id: '',
                    birth_date: '',
                    address: ''
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!newClientData.full_name.trim()) {
                  toast.error('Nome do cliente é obrigatório');
                  return;
                }
                if (!newClientData.apelido.trim()) {
                  toast.error('Apelido é obrigatório');
                  return;
                }
                createClientMutation.mutate(newClientData);
              }}
              className="p-6 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={newClientData.full_name}
                    onChange={(e) => setNewClientData({ ...newClientData, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Nome completo do cliente"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apelido *
                  </label>
                  <input
                    type="text"
                    value={newClientData.apelido}
                    onChange={(e) => setNewClientData({ ...newClientData, apelido: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Como o cliente será chamado"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newClientData.email}
                    onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="email@exemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="text"
                    value={newClientData.phone}
                    onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CPF/CNPJ
                  </label>
                  <input
                    type="text"
                    value={newClientData.document_id}
                    onChange={(e) => setNewClientData({ ...newClientData, document_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="000.000.000-00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Nascimento
                  </label>
                  <input
                    type="date"
                    value={newClientData.birth_date}
                    onChange={(e) => setNewClientData({ ...newClientData, birth_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço
                  </label>
                  <textarea
                    value={newClientData.address}
                    onChange={(e) => setNewClientData({ ...newClientData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    rows={3}
                    placeholder="Endereço completo"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowClientModal(false);
                    setNewClientData({
                      full_name: '',
                      apelido: '',
                      email: '',
                      phone: '',
                      document_id: '',
                      birth_date: '',
                      address: ''
                    });
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createClientMutation.isLoading}
                  className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {createClientMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Cadastrando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Cadastrar Cliente
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Conflito de Horário */}
      {showConflictModal && conflictData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Conflito de Horário</h2>
                  <p className="text-sm text-gray-600">Já existe um agendamento neste horário</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-amber-800 mb-2">Agendamento(s) existente(s):</h3>
                <ul className="space-y-2">
                  {conflictData.map((conflict, index) => (
                    <li key={index} className="text-sm text-amber-700">
                      <div className="font-medium">{conflict.client_name || 'Cliente não especificado'}</div>
                      <div className="text-amber-600">
                        {new Date(conflict.appointment_date).toLocaleString('pt-BR')}
                        {conflict.duration_minutes && ` (${conflict.duration_minutes} min)`}
                      </div>
                      {conflict.notes && <div className="text-amber-600 mt-1">{conflict.notes}</div>}
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-gray-700 mb-4">
                Deseja criar o agendamento mesmo assim? O agendamento existente será sobrescrito.
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowConflictModal(false);
                    setConflictData(null);
                    setPendingAppointmentData(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleForceCreate}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Criando...
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      Criar Mesmo Assim
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!presentedConfirm}
        onConfirm={() => {
          if (presentedConfirm?.action === 'COMPLETED') {
            updateStatusMutation.mutate('COMPLETED');
          } else if (presentedConfirm?.action === 'CANCELLED') {
            updateStatusMutation.mutate('CANCELLED');
          }
          setPresentedConfirm(null);
        }}
        onCancel={() => setPresentedConfirm(null)}
        title={presentedConfirm?.title || ''}
        message={presentedConfirm?.message || ''}
        confirmText="Confirmar"
        cancelText="Cancelar"
        type={presentedConfirm?.type || 'warning'}
      />
    </div>
  );
};

export default AppointmentForm;